import { useState, useEffect } from 'react';
import { CoinListItem } from '../types';
import { cgGet } from '../utils/coingecko';

const CACHE_KEY = 'mycryptoplanner_coin_list_v2';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

interface CoinListCache {
  coins: CoinListItem[];
  fetchedAt: number;
}

function loadCache(): CoinListCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CoinListCache) : null;
  } catch { return null; }
}

function saveCache(coins: CoinListItem[]) {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ coins, fetchedAt: Date.now() }));
}

export function useCoinList() {
  const [coins,   setCoins]   = useState<CoinListItem[]>(() => loadCache()?.coins ?? []);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    async function fetch() {
      const cached = loadCache();
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
        setCoins(cached.coins);
        return;
      }

      setLoading(true);
      try {
        const { data } = await cgGet('/coins/markets', {
          params: {
            vs_currency: 'usd',
            order: 'market_cap_desc',
            per_page: 200,
            page: 1,
            sparkline: false,
          },
          timeout: 12_000,
        });

        const mapped: CoinListItem[] = (data as Record<string, unknown>[]).map((c, i) => ({
          id:            String(c.id),
          symbol:        String(c.symbol).toUpperCase(),
          name:          String(c.name),
          image:         String(c.image),
          marketCapRank: (c.market_cap_rank as number) ?? i + 1,
          currentPrice:  (c.current_price  as number) ?? 0,
          marketCap:     (c.market_cap     as number) ?? 0,
          volume24h:     (c.total_volume   as number) ?? 0,
          high24h:       (c.high_24h       as number) ?? 0,
          low24h:        (c.low_24h        as number) ?? 0,
        }));

        saveCache(mapped);
        setCoins(mapped);
        setError(null);
      } catch {
        const stale = loadCache();
        if (stale) {
          setCoins(stale.coins);
          setError('Using cached coin list.');
        } else {
          setError('Could not load coin list. Check your connection.');
        }
      } finally {
        setLoading(false);
      }
    }

    fetch();
  }, []);

  return { coins, loading, error };
}
